import { readableTextOn, type ResolvedBranding } from '@lkq/shared';

// Applies the resolved branding (docs/09 chain) as CSS custom properties on
// the document root — the ONLY place colours enter the DOM (commandment 10).
// Derived tokens (ink/soft/highlight) are computed from the four stored
// colours so any concept palette gets the full design-system vocabulary.
//
// Typography is UNIVERSAL (Claude Design bundle §1.1: "one stack for all
// concepts; brands never change type") — Public Sans / Archivo / IBM Plex Mono
// are set once in index.css and never overridden per concept. The branding
// schema keeps display_font/body_font for forward-compatibility but they no
// longer drive the instrument design system.

// Bundled fallback favicon (the LKQ mark) — used when a brand supplies none so
// the previous concept's favicon never lingers after a skin switch.
const DEFAULT_FAVICON = '/lkq-logo.svg';

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return [0, 0, 0];
  return [parseInt(m[1].slice(0, 2), 16), parseInt(m[1].slice(2, 4), 16), parseInt(m[1].slice(4, 6), 16)];
}

const toHex = (rgb: [number, number, number]): string =>
  `#${rgb.map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('')}`;

/** Mix a colour towards another (0 = original, 1 = target). */
function mix(hex: string, target: [number, number, number], amount: number): string {
  const rgb = hexToRgb(hex);
  return toHex([
    rgb[0] + (target[0] - rgb[0]) * amount,
    rgb[1] + (target[1] - rgb[1]) * amount,
    rgb[2] + (target[2] - rgb[2]) * amount,
  ]);
}

const darken = (hex: string, amount: number) => mix(hex, [0, 0, 0], amount);
const lighten = (hex: string, amount: number) => mix(hex, [255, 255, 255], amount);

const textColour = (preference: 'auto' | 'light' | 'dark', colour: string): string => {
  const mode = preference === 'auto' ? readableTextOn(colour) : preference;
  return mode === 'light' ? '#FFFFFF' : '#0A0A0A';
};

/** Relative luminance (0 = black, 1 = white). */
function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** Near-black brand primaries (e.g. 1TEC #0A0A0A) can't supply a readable brand
 *  "ink"/highlight by darkening/lightening themselves — derive from accent/
 *  secondary instead, matching the design's per-brand overrides. */
const isVeryDark = (hex: string): boolean => luminance(hex) < 0.03;

export function applyBranding(branding: ResolvedBranding): void {
  const root = document.documentElement;
  const button = branding.button_color ?? branding.primary_color;
  const darkPrimary = isVeryDark(branding.primary_color);

  const tokens: Record<string, string> = {
    '--color-primary': branding.primary_color,
    '--color-primary-ink': darkPrimary ? darken(branding.accent_color, 0.1) : darken(branding.primary_color, 0.25),
    '--color-secondary': branding.secondary_color,
    '--color-accent': branding.accent_color,
    '--color-accent-ink': darken(branding.accent_color, 0.25),
    '--color-accent-soft': lighten(branding.accent_color, 0.88),
    '--color-button': button,
    '--color-focus': darken(button, 0.15),
    '--color-highlight': lighten(darkPrimary ? branding.secondary_color : branding.primary_color, 0.88),
    '--text-on-primary': textColour(branding.active_nav_text_contrast, branding.primary_color),
    '--text-on-secondary': textColour('auto', branding.secondary_color),
    '--text-on-accent': textColour('auto', branding.accent_color),
    '--text-on-button': textColour(branding.button_text_contrast, button),
  };
  for (const [key, value] of Object.entries(tokens)) root.style.setProperty(key, value);

  // Typography is universal (set in index.css) — not overridden per concept.

  // Favicon: always (re)assign so a stale per-concept favicon never persists
  // across a skin switch — when a brand supplies none, reset to the bundled
  // default rather than leaving the previous concept's icon (docs/09 L84).
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = branding.faviconUrl ?? DEFAULT_FAVICON;
}
